/**
 * How long a session has been running, and how to say it.
 *
 * The same rule as `alter-web/infra/lambda/sessions/timer.py`, and
 * deliberately the same shape. Two implementations that drift are worse than
 * one that is slow, so the two test files assert the same boundaries; the
 * browser needs its own only because it repaints every second and cannot ask
 * the server for each tick.
 *
 * `now` is an argument here for the same reason it is there: every
 * interesting case is a boundary, and a function that fetches its own time
 * cannot be tested at one.
 *
 * Times are epoch **seconds**, matching the store. `Date.now() / 1000` at the
 * call site, never milliseconds -- a millisecond value here reads as a
 * session that started fifty thousand years from now.
 */

/** Four hours, then the clock stops. See the Python module for why. */
export const CAP_SECONDS = 4 * 60 * 60;

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

function pausedSeconds(pauses, until) {
  let total = 0;
  for (const pause of pauses ?? []) {
    const at = pause?.at;
    if (!isNumber(at) || at >= until) continue;
    // An open pause runs to `until`: the clock is stopped right now.
    const stop = Math.min(isNumber(pause?.until) ? pause.until : until, until);
    if (stop > at) total += stop - at;
  }
  return total;
}

/**
 * Seconds actually spent: pauses removed, and never past the cap.
 *
 * A session this cannot read reads as zero rather than NaN. A NaN reaching
 * `formatClock` puts "NaN:NaN" on screen, which is a worse answer than a
 * clock that has not started.
 */
export function elapsedSeconds(session, now) {
  const startedAt = session?.startedAt;
  if (!isNumber(startedAt) || !isNumber(now)) return 0;

  const cap = startedAt + CAP_SECONDS;
  const endedAt = session?.endedAt;
  const finish = Math.min(isNumber(endedAt) ? endedAt : now, cap);

  const span = finish - startedAt;
  if (span <= 0) return 0;
  return Math.max(0, span - pausedSeconds(session?.pauses, finish));
}

/**
 * The two kinds of hold. The same words the Lambda uses, and the same
 * default: a pause with no kind on it is a break, because every row written
 * before 2026-08-19 came from a single Pause button that meant a step away
 * from the desk.
 */
export const BREAK = 'break';
export const PAUSE = 'pause';
const DEFAULT_HOLD = BREAK;

/**
 * Seconds inside the sitting that were not work: breaks, not pauses.
 *
 * The clock under the big one, which this screen did not have at all until
 * Allen asked for it. A break is time a sitting somebody is still in actually
 * cost; a pause is the sitting suspended, and counting an overnight here
 * would report a forty-five minute essay as having cost fourteen hours.
 *
 * Mirrors `not_counted_seconds` in the Lambda's timer.py exactly, and for the
 * reason `elapsedSeconds` mirrors its neighbour: the browser repaints every
 * second and cannot ask the server for each tick, so the two have to agree
 * about the arithmetic rather than about the answer.
 */
export function notCountedSeconds(session, now) {
  const finish = isNumber(session?.endedAt) ? session.endedAt : now;
  if (!isNumber(finish)) return 0;
  let total = 0;
  for (const pause of session?.pauses ?? []) {
    if ((pause?.kind ?? DEFAULT_HOLD) !== BREAK) continue;
    const at = pause?.at;
    if (!isNumber(at) || at >= finish) continue;
    const stop = Math.min(isNumber(pause?.until) ? pause.until : finish, finish);
    if (stop > at) total += stop - at;
  }
  return total;
}

/** Which hold is open, or '' when none is. */
export function openHoldKind(session) {
  for (const pause of session?.pauses ?? []) {
    if (isNumber(pause?.at) && !isNumber(pause?.until)) {
      return String(pause?.kind ?? DEFAULT_HOLD);
    }
  }
  return '';
}

/** Floored, never rounded: a second that has not finished has not happened. */
export function formatClock(seconds) {
  const whole = isNumber(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = whole % 60;
  const pad = (n) => String(n).padStart(2, '0');
  // The hours column appears only once there are hours, so a ten-minute break
  // reads 10:05 rather than 0:10:05 -- the same choice the desktop timer made.
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}

/**
 * Which of the timer screen's three faces to draw.
 *
 * `ready` is the one this screen did not used to have: tapping a study block
 * on the calendar opens the timer already knowing which assignment it is for,
 * but not running. A tap that started the clock immediately would be a mis-tap
 * recorded as work on the wrong assignment -- and since only one session may
 * run at a time, undoing it means ending a session you never meant to start,
 * which then feeds the estimates.
 *
 * A running session always wins over a tapped block. Offering to start a
 * second one is an offer the server refuses (one live session, enforced by a
 * conditional write), so showing what IS running is the honest answer and the
 * same one the agenda gives on a conflict.
 */
export function timerScreenState(live, chosen) {
  if (live && live.state !== 'done') return 'running';
  return chosen ? 'ready' : 'empty';
}
